import { motion } from "framer-motion";
import { Fragment } from "react";
import { ArrowLeft, BedDouble, MapPin, Ticket } from "lucide-react";
import { Mark } from "@/components/Mark";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { ACCOMMODATION } from "@/data/copenhagen";
import { useActiveAds, adsForCategory } from "@/lib/ads";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTivoliCard } from "@/components/GetYourGuide";

// Booking.com affiliate link via CJ (lands on Booking, tracked through CJ)
const BOOKING_AFFILIATE_URL = "https://www.anrdoezrs.net/click-101862174-13375717";
// CJ impression pixel — invisible, lets CJ count views of the link
const BOOKING_AFFILIATE_PIXEL = "https://www.ftjcfx.com/image-101862174-13375717";
// CJ deep-link wrapper: per-hotel Booking.com share URLs ride through this
const CJ_DEEP_LINK = "https://www.jdoqocy.com/click-101862174-15734870?url=";

const HERO_IMG =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/vslbxhvq_20260925_190216.webp";
const bookingLink = (url) => `${CJ_DEEP_LINK}${encodeURIComponent(url)}`;
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Editorial one-liners that give each budget tier a warm, human "vibe"
const CATEGORY_VIBE = {
  hostels: "Social, design-led and easy on the wallet",
  "budget-hotels": "Central and budget-smart — walk everywhere",
  "three-star": "Boutique rooms with a local, neighbourhood feel",
  "four-star": "Grown-up comfort and quietly good design",
  "five-star": "Landmark addresses and waterfront luxury",
};
// The editorial favourites — the cards that get the warm highlight (one per tier shown)
const EDITORS_PICKS = new Set(["Villa Copenhagen", "Capsule Hotel Copenhagen", "Imperial Hotel", "ProfilHotels Richmond", "CabInn City"]);

// Map a free-text area label to a real neighbourhood guide (falls back to the hub)
const AREA_PAGES = new Set([
  "indre-by", "christianshavn", "islands-brygge", "refshaleoen", "vesterbro",
  "norrebro", "osterbro", "amager", "frederiksberg", "nordvest", "nordhavn", "carlsberg-byen",
]);
const AREA_ALIASES = {
  nyhavn: "indre-by", "near-nyhavn": "indre-by", norreport: "indre-by", tivoli: "indre-by",
  bernstorffsgade: "indre-by", "city-hall-square": "indre-by", amalienborg: "indre-by",
  vesterport: "indre-by", "central-station": "vesterbro", "by-the-lakes": "norrebro",
};
const areaHref = (area) => {
  const first = (area || "")
    .split("·")[0]
    .trim()
    .toLowerCase()
    .replace(/ø/g, "o")
    .replace(/æ/g, "ae")
    .replace(/å/g, "a")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  const slug = AREA_PAGES.has(first) ? first : AREA_ALIASES[first];
  return slug ? `/areas/${slug}` : "/areas";
};

export default function AccommodationPage() {
  const hotelAds = adsForCategory(useActiveAds(), "Hotel & hostel");
  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) {
      const target = window.__lenis.scroll + el.getBoundingClientRect().top - 90;
      window.__lenis.scrollTo(target);
    } else {
      const y = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <main data-testid="accommodation-page" className="pt-16 md:pt-20">
      <Seo
        title="Where to Stay in Copenhagen — Hotels & Hostels by Budget"
        description="21 hand-picked places to stay in Copenhagen across five budgets — from design hostels to boutique hotels — sorted by neighbourhood."
        path="/stay"
        image="https://images.unsplash.com/photo-1764756719784-7e46c361e0ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
      />
      <PageSchema
        name="Where to Stay in Copenhagen — Hotels & Hostels by Budget"
        description="21 hand-picked places to stay in Copenhagen across five budgets — from design hostels to boutique hotels — sorted by neighbourhood."
        path="/stay"
        crumb="Where to stay"
      />
      {/* page header */}
      <section className="relative bg-[#13141A] text-white overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Dusk on Amagertorv by the Stork Fountain on Strøget, Copenhagen's old-town shopping heart"
            className="w-full h-full object-cover object-[center_28%] opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="accommodation-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="accommodation-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">Hotel suggestions</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            Where to stay<br />
            <span className="font-editorial italic font-600">in Copenhagen</span>
          </motion.h1>
          <p className="font-editorial italic font-600 text-xl md:text-2xl text-white/85 mt-4">
            Every kind of Copenhagen bed.
          </p>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6">
            From design hostels to landmark five-stars — every place on this page is handpicked and
            highly rated, with a spot for every budget. Stay central, walk to everything, and book
            early: the city fills up fast in summer.
          </p>
          <p data-testid="stay-updated" className="font-body text-xs uppercase tracking-[0.2em] text-white/40 mt-4">
            Last updated: 29 September 2026
          </p>

          <div className="mt-8 flex flex-wrap gap-2.5" data-testid="accommodation-jump-nav">
            {ACCOMMODATION.filter((cat) => cat.id !== "hostels").map((cat) => (
              <button
                key={cat.id}
                type="button"
                data-testid={`accommodation-jump-${cat.id}`}
                onClick={() => jumpTo(cat.id)}
                className="font-display font-800 text-sm bg-white/10 text-white border border-white/25 px-5 py-2.5 rounded-full hover:bg-[#DF3226] hover:text-white hover:border-[#DF3226] hover:-translate-y-0.5 transition-all"
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* category sections */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 space-y-16 md:space-y-24">
          {ACCOMMODATION.map((cat, ci) => (
            <Fragment key={cat.id}>
            <div id={cat.id} data-testid={`accommodation-cat-${cat.id}`}>
              <Reveal>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 border-b border-zinc-200 pb-5">
                  <div>
                    <span className="font-body font-800 text-xs tracking-[0.3em] eyebrow" style={{ color: cat.color }}>
                      {cat.kicker.toUpperCase()}
                    </span>
                    <h2 className="font-display font-900 tracking-tighter leading-none text-4xl md:text-5xl mt-2">
                      {cat.label}
                    </h2>
                    {CATEGORY_VIBE[cat.id] && (
                      <p className="font-editorial italic text-base md:text-lg text-black/55 mt-2">
                        {CATEGORY_VIBE[cat.id]}
                      </p>
                    )}
                  </div>
                  <p className="font-body text-sm md:text-base text-black/70 max-w-md">{cat.blurb}</p>
                </div>
              </Reveal>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                {ci === 0 &&
                  hotelAds.map((ad) => (
                    <div
                      key={ad.id}
                      data-testid={`accommodation-partner-${ad.id}`}
                      className="group h-full bg-white border border-zinc-200 shadow-brut p-6 md:p-7 hover:-translate-y-0.5 hover:shadow-xl transition-all"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          {ad.website ? (
                            <a
                              href={ad.website}
                              target="_blank"
                              rel="noopener noreferrer nofollow sponsored"
                              data-testid={`accommodation-partner-link-${ad.id}`}
                              className="font-display font-900 tracking-tight text-2xl md:text-3xl leading-none inline-block underline decoration-1 underline-offset-2 decoration-black/25 hover:decoration-[#DF3226] hover:text-[#DF3226] transition-colors"
                            >
                              {ad.business_name}
                            </a>
                          ) : (
                            <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl leading-none">
                              {ad.business_name}
                            </h3>
                          )}
                          <span className="mt-2 inline-flex items-center gap-1.5 font-body font-700 text-xs tracking-widest" style={{ color: cat.color }}>
                            <MapPin size={13} /> {ad.area.replace(/-/g, " ").toUpperCase()}
                          </span>
                        </div>
                        <span className="shrink-0 font-body font-800 text-[10px] tracking-widest text-[#F59E0B] whitespace-nowrap">
                          SPONSORED
                        </span>
                      </div>
                      <p className="font-body text-sm md:text-base text-black/70 leading-relaxed mt-4">
                        {ad.description}
                      </p>
                    </div>
                  ))}
                {cat.places.map((p, i) => {
                  const isPick = EDITORS_PICKS.has(p.name);
                  return (
                  <Reveal key={p.name} delay={i * 0.05} y={40}>
                    <div
                      data-testid={`accommodation-card-${cat.id}-${i}`}
                      className={`group h-full border shadow-brut p-6 md:p-7 hover:-translate-y-1 hover:shadow-brut-lg transition-all ${
                        isPick ? "bg-[#F6EAD0] border-black" : "bg-white border-zinc-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          {isPick && (
                            <span className="mb-2 inline-flex items-center gap-1 font-body font-800 text-[10px] tracking-widest text-[#B8860B]">
                              OUR PICK
                            </span>
                          )}
                          <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl leading-none">
                            {p.name}
                          </h3>
                          <Link
                            to={areaHref(p.area)}
                            data-testid={`accommodation-area-${slugify(p.name)}`}
                            className={`mt-2 inline-flex items-center gap-1.5 font-body font-700 text-xs tracking-widest underline decoration-transparent underline-offset-2 hover:decoration-current transition-all ${isPick ? "text-black/70" : ""}`}
                            style={isPick ? undefined : { color: cat.color }}
                          >
                            <MapPin size={13} /> {p.area.toUpperCase()}
                          </Link>
                          {p.nearTivoli && (
                            <span
                              data-testid={`accommodation-tivoli-badge-${slugify(p.name)}`}
                              className="mt-1.5 flex items-center gap-1 font-body font-800 text-[10px] tracking-widest text-[#B8860B]"
                            >
                              <Mark size={10} /> STEPS FROM TIVOLI
                            </span>
                          )}
                        </div>
                        <span
                          className={`shrink-0 font-display font-900 text-xs px-2.5 py-1.5 rounded-full whitespace-nowrap ${isPick ? "bg-white text-black border border-black" : "text-white"}`}
                          style={isPick ? undefined : { backgroundColor: cat.color }}
                        >
                          {p.price}
                        </span>
                      </div>
                      <p className={`font-body text-sm md:text-base leading-relaxed mt-4 ${isPick ? "text-black/75" : "text-black/70"}`}>
                        {p.note}
                      </p>
                      {p.link && (
                        <a
                          href={bookingLink(p.link)}
                          target="_blank"
                          rel="noopener noreferrer sponsored"
                          data-testid={`accommodation-book-${slugify(p.name)}`}
                          className={`mt-4 inline-flex items-center gap-1.5 font-body font-800 text-xs px-3.5 py-1.5 rounded-full border transition-all hover:-translate-y-0.5 ${isPick ? "bg-white text-black border-black" : "bg-[#13141A] text-white border-black/10"}`}
                        >
                          Book on Booking.com
                        </a>
                      )}
                    </div>
                  </Reveal>
                  );
                })}
              </div>
            </div>
            </Fragment>
          ))}
          </div>

          {/* compact repeat CTA at the bottom of the listings */}
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-24">
          <Reveal>
            <div className="text-center border-t border-dotted border-zinc-300 pt-10">
              <p className="font-display font-900 tracking-tight text-2xl md:text-3xl">
                Found your favourite?
              </p>
              <a
                href={BOOKING_AFFILIATE_URL}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid="accommodation-booking-cta-bottom"
                className="mt-5 inline-flex items-center gap-2 font-display font-900 text-base md:text-lg bg-[#13141A] text-white px-7 py-3.5 rounded-full border border-black/10 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
              >
                Book your accommodation here
              </a>
              <p className="font-body text-[11px] text-black/40 mt-3">
                Booking.com affiliate link — we may earn a small commission at no extra cost to you.
              </p>
              <img src={BOOKING_AFFILIATE_PIXEL} width="1" height="1" border="0" alt="" style={{ position: "absolute", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-white/20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Got a bed sorted?<br />
            <span className="font-editorial italic font-600">Now build the trip</span>
          </h2>
          <a
            href="/#planner"
            data-scroll="#planner"
            data-testid="accommodation-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full border border-white/20 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            Open the planner
          </a>
        </div>
      </section>

      {/* pricing note — lives at the foot of the page */}
      <section className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8">
          <p data-testid="stay-pricing-note" className="font-body text-xs md:text-sm text-black/55 max-w-2xl">
            Rough starting rates per night — hostels priced per bed (1 person), hotels per double
            room (2 people, private bathroom). Real prices swing with season and demand.
          </p>
        </div>
      </section>

      {/* GetYourGuide Tivoli ticket — takes the ad slot on this page
          (a Booking.com search here duplicated the per-hotel links above) */}
      <section className="border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
          <div className="flex items-center gap-2 mb-6">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">TOURS &amp; TICKETS</p>
          </div>
          <div className="max-w-2xl">
            <GygTivoliCard testid="accommodation-gyg-tivoli" />
          </div>
        </div>
      </section>
      <ExploreLinks
        testid="explore-stay"
        links={[
          { label: "Know the neighbourhood first — the area guides", to: "/areas" },
          { label: "Travelling cheap — Copenhagen on a budget", to: "/budget" },
          { label: "Bringing the kids? The complete family guide", to: "/copenhagen-with-kids" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
